import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { fetchMe, type WorkHoursMe } from "../services/workHoursService";
import { fetchDailyNotes, generateDailyNotes, type DailyNote, type DailyNotesResponse } from "../services/workHoursFeedService";
import { hourLabel, localeOf, presetRange, today, type RangePreset } from "../lib/heatmap";
import {
  describeRequestFailure,
  failureMessage,
  type FailureKeys,
  type FailureMessage,
  type RequestFailure,
} from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Bitácora (#528): el feed del día a día. Un resumen por persona y por día, en
// lenguaje llano —por qué se hizo y qué se hizo—, sin identificadores técnicos y
// con tope de 250 palabras. Lo redacta Claude en la corrida nocturna a partir de
// las franjas y los mensajes de trabajo que ya guarda el módulo.
//
// Dos cosas que la pantalla NO puede esconder:
//  · Un resumen sin redacción (la IA no estaba disponible) se marca como tal, en
//    otro tono y con su motivo: no se hace pasar por un resumen redactado.
//  · Cargando, vacío y error son tres estados distintos. Un fallo de red no se
//    lee como «ese día nadie trabajó».
//  · Un fallo de la redacción a demanda no inventa su causa: de permisos solo se
//    habla con un 403, y si no hubo respuesta (red o tiempo de espera) se dice
//    que el resultado no se pudo CONFIRMAR —no que no se redactó— y se relee el
//    feed, porque lo más probable es que el servidor lo haya terminado.
//
// Un dev ve su bitácora; un administrador ve a todo el equipo, filtra por
// persona y puede pedir la redacción de un día a demanda.
// ---------------------------------------------------------------------------

/** `2026-09-15` → `martes, 15 de septiembre de 2026` (o su equivalente en inglés). */
function longDate(date: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  // Fecha local explícita: `new Date("2026-09-15")` se lee como UTC y en América
  // pinta el día anterior.
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** Motivos de degradación que la pantalla sabe explicar; el resto cae en «unknown». */
const KNOWN_REASONS = ["ai_not_configured", "ai_failed", "ai_empty_output", "ai_invalid_output"];
const reasonKey = (reason: string | null) => (reason && KNOWN_REASONS.includes(reason) ? reason : "unknown");

/** Texto i18n (clave + variables) que se puede guardar en el estado sin congelar el idioma. */
type Message = FailureMessage;

/** Las claves de esta sección, una por motivo (ver `failureMessage`). */
const GENERATE_KEYS: FailureKeys = {
  failed: "workHours.journal.generateError",
  permission: "workHours.journal.generateErrorForbidden",
  auth: "workHours.journal.generateErrorAuth",
  unconfirmed: "workHours.journal.generateUnconfirmed",
  server: "workHours.journal.generateErrorServer",
};

/**
 * Qué decirle a la persona cuando la redacción a demanda no salió bien.
 *
 * Nada de causas inventadas: de permisos solo se habla con un 403, y si el
 * servidor mandó su propio mensaje, ese manda. Cuando no hubo respuesta (red o
 * tiempo de espera) el resultado es DESCONOCIDO —el servidor pudo terminar el
 * trabajo—, así que se dice justo eso y se invita a recargar.
 */
export function generateFailureMessage(failure: RequestFailure, opts: { date: string }): Message {
  return failureMessage(failure, GENERATE_KEYS, { date: opts.date });
}

const groupByDate = (notes: DailyNote[]): { date: string; notes: DailyNote[] }[] => {
  const groups: { date: string; notes: DailyNote[] }[] = [];
  for (const n of notes) {
    const last = groups[groups.length - 1];
    if (last && last.date === n.date) last.notes.push(n);
    else groups.push({ date: n.date, notes: [n] });
  }
  return groups;
};

export default function JournalSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const locale = localeOf(i18n?.language);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;

  const [me, setMe] = useState<WorkHoursMe | null>(null);
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [range, setRange] = useState(presetRange("7d"));
  const [userId, setUserId] = useState("");
  const [data, setData] = useState<DailyNotesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Se guarda la CLAVE i18n, no el texto: así `load` no depende de `t` (que
  // react-i18next reemplaza al cargar un namespace o al cambiar de idioma).
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Generación a demanda (admin).
  const [genDate, setGenDate] = useState(today());
  const [force, setForce] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noticeKey, setNoticeKey] = useState<Message | null>(null);
  // El fallo de la REDACCIÓN vive aparte del fallo del FEED: al recargar tras un
  // tiempo de espera agotado, `load` limpia su propio error y este aviso —el
  // único que explica lo que pasó— tiene que sobrevivir a esa recarga.
  const [genErrorKey, setGenErrorKey] = useState<Message | null>(null);

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    try {
      setData(await fetchDailyNotes({ from: range.from, to: range.to, userId: userId || undefined }));
    } catch {
      // Un fallo NO es un feed vacío: se borra lo anterior y se marca el error.
      setData(null);
      setErrorKey("workHours.journal.loadError");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    setConfirming(false);
    setBusy(true);
    setNoticeKey(null);
    setGenErrorKey(null);
    try {
      const r = await generateDailyNotes({ date: genDate, userId: userId || undefined, force });
      setNoticeKey({
        key: r.degraded > 0 ? "workHours.journal.generatedDegraded" : "workHours.journal.generated",
        opts: { generated: r.generated, skipped: r.skipped, degraded: r.degraded, date: genDate },
      });
      await load();
    } catch (e) {
      const failure = describeRequestFailure(e);
      // Sin respuesta no se puede negar el resultado: la redacción pudo haber
      // terminado del lado del servidor (pasó: 201 y cuatro resúmenes escritos
      // mientras la pantalla decía que no). Se relee el feed ANTES de opinar, y
      // así los resúmenes que sí se escribieron aparecen junto al aviso.
      if (failure.kind === "unconfirmed") await load();
      setGenErrorKey(generateFailureMessage(failure, { date: genDate }));
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => groupByDate(data?.notes ?? []), [data]);
  const isEmpty = !loading && !errorKey && groups.length === 0;

  return (
    <div className="w-full max-w-5xl overflow-y-auto p-4 sm:p-6 space-y-4">
      {/* Encabezado y filtros */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.journal")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>
            {data?.scope === "team" ? t("workHours.scopeTeam") : t("workHours.scopeMine")} · {t("workHours.journal.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* «Hoy» solo para los dueños (#1138). */}
          {((me?.isAdmin ? ["today", "7d", "30d"] : ["7d", "30d"]) as Array<"today" | "7d" | "30d">).map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`range-${p}`}
              onClick={() => {
                setPreset(p);
                setRange(presetRange(p));
              }}
              className={`rounded-lg px-3 py-2 text-xs font-medium ${
                preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
              }`}
            >
              {t(`workHours.range.${p}`)}
            </button>
          ))}
          <input
            type="date"
            aria-label={t("workHours.filters.from")}
            value={range.from}
            max={range.to}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, from: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <input
            type="date"
            aria-label={t("workHours.filters.to")}
            value={range.to}
            min={range.from}
            max={today()}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, to: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          {me?.isAdmin && (
            <select aria-label={t("workHours.filters.user")} value={userId} onChange={(e) => setUserId(e.target.value)} className={inputClass(isDarkMode)}>
              <option value="">{t("workHours.filters.team")}</option>
              {(data?.users ?? []).map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.userName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Generación a demanda: solo administrador y con confirmación, porque
          cada resumen cuesta una llamada al modelo. */}
      {me?.isAdmin && (
        <div className={`${card} flex flex-wrap items-center gap-2`} data-testid="journal-generate">
          <span className={`text-sm font-semibold ${tc.text}`}>{t("workHours.journal.generateTitle")}</span>
          <input
            type="date"
            aria-label={t("workHours.journal.generateDate")}
            value={genDate}
            max={today()}
            onChange={(e) => setGenDate(e.target.value)}
            className={inputClass(isDarkMode)}
          />
          <label className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            {t("workHours.journal.force")}
          </label>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={busy || confirming}
            className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
          >
            <i className="ri-quill-pen-line mr-1" />
            {busy ? t("workHours.saving") : t("workHours.journal.generate")}
          </button>
          <span className={`text-xs ${tc.textSecondary}`}>{t("workHours.journal.generateHint")}</span>
          {/* Redactar un día es una llamada al modelo por persona: se avisa que
              tarda, en vez de dejar un botón mudo que parece colgado. */}
          {busy && (
            <span data-testid="journal-generate-busy" className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
              <i className="ri-loader-4-line animate-spin" />
              {t("workHours.journal.generateSlow")}
            </span>
          )}
        </div>
      )}

      {confirming && (
        <div data-testid="journal-confirm" className={`${card} border-amber-500/50`}>
          <p className={`text-sm font-semibold ${tc.text}`}>
            <i className="ri-alert-line mr-1 text-amber-500" />
            {t("workHours.journal.confirmTitle")}
          </p>
          <p className={`mt-1 text-sm ${tc.textSecondary}`}>
            {t(force ? "workHours.journal.confirmForce" : "workHours.journal.confirmBody", { date: genDate })}
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => void generate()} disabled={busy} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50">
              {t("workHours.journal.confirmRun")}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={`rounded-lg border px-3 py-2 text-xs font-medium ${tc.border} ${tc.text}`}>
              {t("workHours.cancel")}
            </button>
          </div>
        </div>
      )}

      {noticeKey && (
        <p data-testid="journal-notice" className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600">
          {t(noticeKey.key, noticeKey.opts)}
        </p>
      )}
      {errorKey && (
        <p data-testid="journal-error" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
          {t(errorKey)}
        </p>
      )}
      {/* Fallo de la redacción a demanda. Ámbar y no rojo cuando el resultado
          quedó sin confirmar: no se sabe que haya fallado nada. */}
      {genErrorKey && (
        <div
          data-testid="journal-generate-error"
          className={`rounded-lg p-3 text-sm ${
            genErrorKey.key === "workHours.journal.generateUnconfirmed" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"
          }`}
        >
          <p>{t(genErrorKey.key, genErrorKey.opts)}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90"
          >
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}

      {loading && (
        <div data-testid="journal-loading" className={`${card} text-sm ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-1 animate-spin" />
          {t("workHours.journal.loading")}
        </div>
      )}

      {isEmpty && (
        <div data-testid="journal-empty" className={`${card} text-center`}>
          <i className={`ri-book-open-line text-2xl ${tc.textSecondary}`} />
          <p className={`mt-2 text-sm font-semibold ${tc.text}`}>{t("workHours.journal.emptyTitle")}</p>
          <p className={`mt-1 text-sm ${tc.textSecondary}`}>{t("workHours.journal.emptyBody")}</p>
        </div>
      )}

      {/* El feed: un bloque por día, una tarjeta por persona. */}
      {groups.map((group) => (
        <section key={group.date} data-testid={`journal-day-${group.date}`} className="space-y-2">
          <h2 className={`text-sm font-semibold uppercase tracking-wide ${tc.textSecondary}`}>{longDate(group.date, locale)}</h2>
          {group.notes.map((note) => (
            <article key={note.id} data-testid={`journal-note-${note.userId}-${note.date}`} className={card}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-sm font-semibold ${tc.text}`}>{note.userName || t("workHours.journal.unknownPerson")}</span>
                <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] text-violet-500">
                  {t("workHours.journal.hours", { count: note.hours })}
                </span>
                {typeof note.firstHour === "number" && typeof note.lastHour === "number" && (
                  <span className={`text-[11px] ${tc.textSecondary}`}>
                    {hourLabel(note.firstHour)}–{hourLabel((note.lastHour + 1) % 24)}
                  </span>
                )}
                {note.status === "fallback" ? (
                  <span data-testid="journal-status-fallback" className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-600">
                    <i className="ri-error-warning-line mr-1" />
                    {t("workHours.journal.statuses.fallback")}
                  </span>
                ) : (
                  <span data-testid="journal-status-ai" className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-600">
                    {t("workHours.journal.statuses.ai_generated")}
                  </span>
                )}
              </div>

              {note.projects && note.projects.length > 0 && (
                <p className={`mt-1 text-xs ${tc.textSecondary}`}>
                  {t("workHours.journal.projects")}: {note.projects.join(" · ")}
                </p>
              )}

              <p className={`mt-2 whitespace-pre-line text-sm leading-relaxed ${note.status === "fallback" ? tc.textSecondary : tc.text}`}>{note.text}</p>

              {note.status === "fallback" && (
                <p className="mt-2 rounded-lg bg-amber-500/10 p-2 text-xs text-amber-600">
                  {t(`workHours.journal.reasons.${reasonKey(note.fallbackReason)}`)}
                </p>
              )}

              <p className={`mt-2 text-[11px] ${tc.textSecondary}`}>{t("workHours.journal.words", { count: note.wordCount })}</p>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
